import { useState, useEffect } from "react"
import { Badge } from "@/shared/components/ui/badge"
import { Button } from "@/shared/components/ui/button"
import { RotateCcw, Search, ChevronLeft, ChevronRight, MoreVertical, Trash2, Eye } from "lucide-react"
import { Input } from "@/shared/components/ui/input"
import { Checkbox } from "@/shared/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import { useToast } from "@/shared/components/Toast"
import { AdminPageHeader, AdminSectionCard } from "@/features/admin/components/common/layout"

interface Message {
  id: number
  name: string
  email: string
  phone?: string
  subject: string
  best_time_to_call?: string
  message: string
  created_at: string
  deleted_at: string
  status: 'new' | 'read' | 'replied' | 'archived'
}

export default function DeletedMessages() {

  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const { showToast } = useToast()

  useEffect(() => {
    fetchMessages()
  }, [statusFilter, currentPage, itemsPerPage])

  const fetchMessages = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        deleted: 'true', // Fetch only deleted messages
        limit: itemsPerPage.toString(),
        offset: ((currentPage - 1) * itemsPerPage).toString(),
      })
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter)
      }

      const response = await fetch(`/api/contact-messages.php?${params}`)
      const data = await response.json()
      
      if (data.success) {
        setMessages(data.data || [])
        setTotalRecords(data.total || 0)
      } else {
        showToast({
          type: 'error',
          title: 'Error',
          message: data.message || 'Failed to load deleted messages'
        })
      }
    } catch (error) {
      console.error('Error fetching deleted messages:', error)
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to load deleted messages'
      })
    } finally {
      setLoading(false)
    }
  }

  const restoreMessage = async (id: number) => {
    if (!confirm('Are you sure you want to restore this message?')) return
    
    try {
      const response = await fetch('/api/contact-messages.php', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, restore: true })
      })
      
      const data = await response.json()
      if (data.success) {
        setMessages(messages.filter(msg => msg.id !== id))
        setSelectedIds(selectedIds.filter(selectedId => selectedId !== id))
        showToast({
          type: 'success',
          title: 'Success',
          message: 'Message restored successfully'
        })
      } else {
        showToast({
          type: 'error',
          title: 'Error',
          message: data.message || 'Failed to restore message'
        })
      }
    } catch (error) {
      console.error('Error restoring message:', error)
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to restore message'
      })
    }
  }

  const restoreSelectedMessages = async () => {
    if (selectedIds.length === 0) return
    if (!confirm(`Are you sure you want to restore ${selectedIds.length} selected message(s)?`)) return

    try {
      const promises = selectedIds.map(id =>
        fetch('/api/contact-messages.php', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, restore: true })
        })
      )

      await Promise.all(promises)
      
      setMessages(messages.filter(msg => !selectedIds.includes(msg.id)))
      setSelectedIds([])
      showToast({
        type: 'success',
        title: 'Success',
        message: `${selectedIds.length} message(s) restored successfully`
      })
    } catch (error) {
      console.error('Error restoring messages:', error)
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to restore selected messages'
      })
    }
  }

  const viewMessage = (message: Message) => {
    setSelectedMessage(message)
    setViewDialogOpen(true)
  }

  const toggleSelectMessage = (id: number) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    )
  }

  const selectAllMessages = (checked: boolean) => {
    if (checked) {
      setSelectedIds(messages.map(msg => msg.id))
    } else {
      setSelectedIds([])
    }
  }

  const filteredMessages = messages.filter(message =>
    message.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.subject.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const totalPages = Math.ceil(totalRecords / itemsPerPage)

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      new: { color: 'bg-blue-100 text-blue-700', label: 'New' },
      read: { color: 'bg-green-100 text-green-700', label: 'Read' },
      replied: { color: 'bg-purple-100 text-purple-700', label: 'Replied' },
      archived: { color: 'bg-gray-100 text-gray-700', label: 'Archived' }
    }
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.new
    return <Badge className={`${config.color} text-xs`}>{config.label}</Badge>
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Deleted Messages"
        description="Manage deleted contact messages - restore or permanently remove them"
        icon={Trash2}
        badge={totalRecords > 0 ? { label: `${totalRecords} Deleted`, variant: "destructive" } : undefined}
      />

      <AdminSectionCard>
        <div className="p-6 pb-4">
          {/* Search and Filters */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                  <SelectItem value="replied">Replied</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={restoreSelectedMessages}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Restore Selected ({selectedIds.length})
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedIds.length === messages.length && messages.length > 0}
                onCheckedChange={selectAllMessages}
              />
              <span className="text-sm text-gray-600">
                {selectedIds.length > 0 ? `${selectedIds.length} selected` : `${totalRecords} deleted message(s)`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show</span>
              <Select value={itemsPerPage.toString()} onValueChange={(value) => setItemsPerPage(parseInt(value))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-600">entries</span>
            </div>
          </div>
        </div>

        <div className="p-6 pt-0">
          {loading ? (
            <div className="text-center py-8">Loading deleted messages...</div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No deleted messages found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedIds.length === filteredMessages.length && filteredMessages.length > 0}
                        onCheckedChange={selectAllMessages}
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Deleted Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMessages.map((message) => (
                    <TableRow key={message.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(message.id)}
                          onCheckedChange={() => toggleSelectMessage(message.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{message.name}</TableCell>
                      <TableCell>{message.email}</TableCell>
                      <TableCell className="max-w-xs truncate">{message.subject}</TableCell>
                      <TableCell>{getStatusBadge(message.status)}</TableCell>
                      <TableCell>
                        {new Date(message.deleted_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewMessage(message)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => restoreMessage(message.id)}>
                              <RotateCcw className="h-4 w-4 mr-2" />
                              Restore
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} entries
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </AdminSectionCard>

      {/* View Message Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-labelledby="view-deleted-message-title" aria-describedby="view-deleted-message-desc">
          <DialogHeader>
            <VisuallyHidden>
              <DialogTitle id="view-deleted-message-title" className="flex items-center gap-2">
                Deleted Message Details
              </DialogTitle>
            </VisuallyHidden>
            <DialogDescription id="view-deleted-message-desc">
              Message from {selectedMessage?.name} - Deleted on {selectedMessage?.deleted_at ? new Date(selectedMessage.deleted_at).toLocaleDateString() : 'N/A'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1">{selectedMessage.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1">{selectedMessage.email}</p>
                </div>
                {selectedMessage.phone && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Phone</label>
                    <p className="mt-1">{selectedMessage.phone}</p>
                  </div>
                )}
                {selectedMessage.best_time_to_call && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Best Time to Call</label>
                    <p className="mt-1">{selectedMessage.best_time_to_call}</p>
                  </div>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Subject</label>
                <p className="mt-1">{selectedMessage.subject}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Message</label>
                <p className="mt-1 whitespace-pre-wrap bg-gray-50 p-3 rounded border">
                  {selectedMessage.message}
                </p>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <div className="mt-1">{getStatusBadge(selectedMessage.status)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Submitted</label>
                    <p className="mt-1 text-sm text-gray-600">
                      {new Date(selectedMessage.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Deleted</label>
                    <p className="mt-1 text-sm text-gray-600">
                      {new Date(selectedMessage.deleted_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => restoreMessage(selectedMessage.id)}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Restore Message
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}